/* Smart Cart brain (sc-): pure suggestion logic for the cart drawer. Copied from sn-smart-nudge.js (dictionaries,
   traitsOf, saving, scoring incl. no-downsell / upgrade / small add-on, pick, message bank, chooseLine, chipModel /
   resolveVariant, sn_v1 learning) and based on the CART instead of the current product page:
   - every cart line (except Talbeena add-on lines) is an "anchor"; a product is scored against each anchor, best wins;
   - candle anchors only allow candles + Talbeena (food); Talbeena and other anchors allow every category;
   - a cart with only candles uses the candle picker (upgrade / Talbeena first), everything else the greedy picker.
   Excluded: products already in the cart, Talbeena add-on packs, webinar, tags no-upsell / hidden, gift cards,
   price 0 (so the ₹0 Glow Atelier builder is never a card; BYO singles link to it instead), unavailable.
   No DOM here; sc-smart-cart.js renders. Exposed as window.ScBrain. */
window.ScBrain = (() => {
  /* ---------- Keyword dictionaries (same as sn-smart-nudge.js) ---------- */
  const TYPE_MAP = {
    candle: 'candles',
    'candle combo': 'candles',
    'food & nutrition': 'food',
    stickers: 'decor',
    cosmetics: 'cosmetics',
    'learning & stationery': 'learning',
  };
  const TYPE_MOODS = {
    stickers: ['decor', 'faith'],
  };
  const CATEGORY_MOODS = {
    candles: ['glow'],
    decor: ['decor'],
    cosmetics: ['selfcare'],
    learning: ['kids', 'learning'],
  };
  const CATEGORY_OCCASIONS = {
    cosmetics: ['gifting'],
    learning: ['gifting'],
  };
  const CATEGORY_WORDS = {
    candles: ['candle', 'candles', 'diya', 'diyas', 'deeya', 'tealight', 'tea light', 'tea-light', 'glow box', 'glow combo', 'मोमबत्ती', 'कैंडल', 'दीया', 'दिया'],
    food: ['talbeena', 'talbina', 'food', 'nutrition', 'cereal', 'cereals', 'superfood', 'barley', 'jau', 'तलबीना', 'जौ'],
    decor: ['sticker', 'stickers', 'decor', 'decoration', 'wall art', 'स्टिकर', 'सजावट'],
    cosmetics: ['cosmetic', 'cosmetics', 'skin care', 'skincare', 'hair care', 'haircare', 'shampoo', 'face wash', 'serum', 'त्वचा'],
    learning: ['tracing', 'handwriting', 'grooved', 'workbook', 'stationery', 'learning', 'practice book', 'पढ़ाई'],
  };
  const MOOD_WORDS = {
    glow: ['candle', 'candles', 'diya', 'deeya', 'roshni', 'tealight', 'tea light', 'glow box', 'glow combo', 'light up', 'deepak', 'रोशनी', 'दीया', 'दिया', 'दीपक', 'मोमबत्ती'],
    sweet: ['sweet', 'meetha', 'mitha', 'mithai', 'chocolate', 'choco', 'mango', 'strawberry', 'vanilla', 'mawa', 'kheer', 'मीठा', 'मिठाई', 'चॉकलेट'],
    faith: ['dua', 'duas', 'sunnah', 'islamic', 'quran', 'namaz', 'salah', 'bismillah', 'masjid', 'prayer', 'दुआ', 'सुन्नत', 'नमाज़', 'नमाज'],
    decor: ['decor', 'decorative', 'sticker', 'stickers', 'sajawat', 'सजावट'],
    gift: ['gift', 'gifting', 'gift-ready', 'tohfa', 'surprise gift', 'तोहफा', 'तोहफ़ा', 'उपहार'],
    comfort: ['cozy', 'cosy', 'warm', 'comfort', 'sukoon', 'relax', 'सुकून'],
    selfcare: ['self-care', 'self care', 'selfcare', 'skin care', 'skincare', 'hair care', 'haircare', 'pamper'],
    kids: ['kids', 'kid', 'children', 'child', 'bachche', 'bachchon', 'बच्चे', 'बच्चों'],
    learning: ['learning', 'learn', 'handwriting', 'tracing', 'practice', 'letters', 'पढ़ाई'],
  };
  const OCCASION_WORDS = {
    diwali: ['diwali', 'deepawali', 'deepavali', 'दिवाली', 'दीपावली'],
    festive: ['festive', 'festival', 'festivals', 'tyohar', 'tyohaar', 'celebration', 'त्योहार', 'त्यौहार'],
    ramadan: ['ramadan', 'ramzan', 'ramazan', 'iftar', 'sehri', 'suhoor', 'रमज़ान', 'रमजान', 'इफ्तार', 'सेहरी'],
    eid: ['eid', 'eidi', 'ईद'],
    gifting: ['gift', 'gifting', 'gift-ready', 'tohfa', 'तोहफा', 'तोहफ़ा', 'उपहार'],
    daily: ['daily', 'everyday', 'every day', 'roz', 'breakfast', 'nashta', 'रोज़', 'रोज', 'नाश्ता'],
  };
  const FESTIVAL_OCCASIONS = { diwali: ['diwali', 'festive'], eid: ['eid', 'festive'], ramadan: ['ramadan'], custom: ['festive'] };
  const COMPLEMENT = [
    ['glow', 'sweet'], ['glow', 'faith'], ['sweet', 'faith'],
    ['glow', 'selfcare'], ['kids', 'sweet'], ['kids', 'faith'], ['learning', 'faith'],
  ];
  // Candle anchors only pull these categories (cart rule from the brief).
  const CANDLE_CATEGORIES = ['candles', 'food'];
  const PREFERRED_PAIRS = ['candles>food', 'candles>candles', 'food>candles', 'food>decor', 'decor>candles', 'decor>food'];
  const SMALL_ADDON_SHARE = 0.4;
  // Talbeena add-on packs: never suggested (they belong to a combo, cart.js keeps add-on ≤ combo).
  const ADDON_HANDLES = ['takmeeli-talbeena-add-on', 'nutri-nuts-talbeena-add-on'];

  /* ---------- Message bank (same as sn-smart-nudge.js; one emoji per line) ---------- */
  const BANK = [
    ['f01', 'glow', 'sweet', 'hinglish', '[festival] ki roshni ho gayi set 🪔 Ab thoda meetha ho jaye?'],
    ['f02', 'glow', 'sweet', 'hindi', '[festival] की रोशनी तैयार 🪔 अब थोड़ा मीठा हो जाए?'],
    ['f03', 'glow', 'sweet', 'english', 'Your [festival] glow is sorted 🪔 Something sweet to go with it?'],
    ['f04', 'glow', 'sweet', 'hinglish', 'Roshni ho gayi set 🪔 Ab thoda meetha ho jaye?'],
    ['f05', 'glow', 'sweet', 'hindi', 'रोशनी तैयार 🪔 अब थोड़ा मीठा हो जाए?'],
    ['f06', 'glow', 'sweet', 'english', 'Glow sorted 🪔 Something sweet to go with it?'],
    ['f07', 'sweet', 'glow', 'hinglish', 'Meetha toh le liya, ab ghar ko bhi chamkao ✨'],
    ['f08', 'sweet', 'glow', 'hindi', 'मीठा तो ले लिया, अब घर को भी रोशन करो ✨'],
    ['f09', 'sweet', 'glow', 'english', 'Sweet side sorted. Now light up the room too ✨'],
    ['f10', 'glow', 'faith', 'hinglish', 'Ghar ki roshni ke saath ek dua bhi 🤲'],
    ['f11', 'glow', 'faith', 'hindi', 'घर की रोशनी के साथ एक दुआ भी 🤲'],
    ['f12', 'glow', 'faith', 'english', 'Light for the home, a dua for the heart 🤲'],
    ['f13', 'faith', 'glow', 'hinglish', 'Duaon ke saath thodi roshni bhi ho jaye? 🪔'],
    ['f14', 'faith', 'glow', 'hindi', 'दुआओं के साथ थोड़ी रोशनी भी हो जाए? 🪔'],
    ['f15', 'faith', 'glow', 'english', 'Pair your duas with a little glow 🪔'],
    ['f16', 'faith', 'sweet', 'hinglish', 'Dua ke saath thoda meetha bhi? 😊'],
    ['f17', 'faith', 'sweet', 'hindi', 'दुआ के साथ थोड़ा मीठा भी? 😊'],
    ['f18', 'faith', 'sweet', 'english', 'A dua and something sweet. Nice pair 😊'],
    ['f19', 'any', 'upgrade', 'hinglish', 'Sirf [extra] aur mein [name] 🎁'],
    ['f20', 'any', 'upgrade', 'hindi', 'बस [extra] और में [name] 🎁'],
    ['f21', 'any', 'upgrade', 'english', 'Just [extra] more for [name] 🎁'],
    ['f22', 'gift', 'any', 'english', 'Gifting? Add this and double the smile 😄'],
    ['f23', 'gift', 'any', 'hinglish', 'Gift de rahe ho? Ye bhi saath rakh do 😄'],
    ['f24', 'gift', 'any', 'hindi', 'तोहफ़ा दे रहे हो? ये भी साथ रख दो 😄'],
    ['f25', 'any', 'any', 'hinglish', '[name] par [save] ki bachat 🙌'],
    ['f26', 'any', 'any', 'hindi', '[name] पर [save] की बचत 🙌'],
    ['f27', 'any', 'any', 'english', '[name] comes with [save] off 🙌'],
    ['f28', 'any', 'any', 'hinglish', '[festival] ki taiyari? Ye bhi list mein daal lo 📝'],
    ['f29', 'any', 'any', 'hindi', '[festival] की तैयारी? ये भी लिस्ट में डाल लो 📝'],
    ['f30', 'any', 'any', 'english', 'Getting [festival]-ready? Put this on the list 📝'],
    ['f31', 'any', 'any', 'hinglish', '[name] bhi saath mein? Sab milake [total] 👀'],
    ['f32', 'any', 'any', 'hindi', '[name] भी साथ में? सब मिलाकर [total] 👀'],
    ['f33', 'any', 'any', 'english', 'Add [name] too? All together: [total] 👀'],
    ['f34', 'any', 'cosmetics', 'hinglish', 'Thoda apna khayal bhi? [name] dekh lo 💆'],
    ['f35', 'any', 'cosmetics', 'hindi', 'थोड़ा अपना ख़्याल भी? [name] देख लो 💆'],
    ['f36', 'any', 'cosmetics', 'english', 'A little me-time pick: [name] 💆'],
    ['f37', 'cosmetics', 'any', 'hinglish', 'Apne liye le liya, ab ghar ke liye bhi kuch? 🏡'],
    ['f38', 'cosmetics', 'any', 'hindi', 'अपने लिए ले लिया, अब घर के लिए भी कुछ? 🏡'],
    ['f39', 'cosmetics', 'any', 'english', 'Something for you is sorted. Something for home too? 🏡'],
    ['f40', 'any', 'learning', 'hinglish', 'Bachchon ke liye bhi kuch? [name] dekho ✏️'],
    ['f41', 'any', 'learning', 'hindi', 'बच्चों के लिए भी कुछ? [name] देखो ✏️'],
    ['f42', 'any', 'learning', 'english', 'Something for the kids too? See [name] ✏️'],
    ['f43', 'learning', 'any', 'hinglish', 'Bachchon ka ho gaya, ab poori family ke liye kuch? 👨‍👩‍👧'],
    ['f44', 'learning', 'any', 'hindi', 'बच्चों का हो गया, अब पूरी फ़ैमिली के लिए कुछ? 👨‍👩‍👧'],
    ['f45', 'learning', 'any', 'english', 'Kids sorted. Something for the whole family? 👨‍👩‍👧'],
    ['f46', 'candles', 'single', 'hinglish', 'Ek aur roshni? [name] bhi saath le lo 🕯️'],
    ['f47', 'candles', 'single', 'hindi', 'एक और रोशनी? [name] भी साथ ले लो 🕯️'],
    ['f48', 'candles', 'single', 'english', 'One more glow? Add [name] 🕯️'],
    ['f49', 'candles', 'single', 'hinglish', 'Box ke saath ek chhoti si candle aur ✨'],
    ['f50', 'candles', 'single', 'hindi', 'बॉक्स के साथ एक छोटी सी कैंडल और ✨'],
    ['f51', 'candles', 'single', 'english', 'A little extra candle to go with your box ✨'],
    ['f52', 'any', 'set', 'hinglish', 'Ek poora candle set bhi dekh lo? [name] 🕯️'],
    ['f53', 'any', 'set', 'hindi', 'एक पूरा कैंडल सेट भी देख लो? [name] 🕯️'],
    ['f54', 'any', 'set', 'english', 'How about a full candle set? [name] 🕯️'],
    // Talbeena cart -> a small BYO candle (one-tap, no box minimum with a Talbeena combo in the cart)
    ['f55', 'food', 'single', 'hinglish', 'Talbeena ke saath ek chhoti si roshni bhi? 🕯️'],
    ['f56', 'food', 'single', 'hindi', 'तलबीना के साथ एक छोटी सी रोशनी भी? 🕯️'],
    ['f57', 'food', 'single', 'english', 'A little glow to go with your Talbeena? 🕯️'],
  ].map(([id, from, to, lang, text]) => ({ id, from, to, lang, text }));

  const LANGS = ['hinglish', 'hindi', 'english'];
  const LANG_ATTR = { hinglish: 'hi-Latn', hindi: 'hi', english: 'en' };
  const MIN_SCORE = 20;
  const MIN_SAVE_PCT = 5;
  const PRICEY = 1.5;
  const TOO_PRICEY = 2.5;
  const CHIP_MAX_OPTIONS = 3;
  const CHIP_MAX_VARIANTS = 24;
  const DAY = 86400000;

  /* ---------- Storage (wrapped: private mode / blocked storage just means no learning) ---------- */
  const store = {
    get(area, key, fallback) {
      try {
        return JSON.parse(window[area].getItem(key)) || fallback;
      } catch {
        return fallback;
      }
    },
    set(area, key, value) {
      try {
        window[area].setItem(key, JSON.stringify(value));
      } catch {}
    },
  };
  const today = (d = new Date()) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  // sn_v1 (localStorage, shared with Smart Nudge): views, per-pair counters, lines shown, clicked suggestions.
  const visitor = store.get('localStorage', 'sn_v1', null) || {};
  visitor.first = visitor.first || today();
  visitor.views = visitor.views || {};
  visitor.pairs = visitor.pairs || {};
  visitor.lines = visitor.lines || {};
  visitor.clicked = visitor.clicked || {};
  const saveVisitor = () => {
    const hs = Object.keys(visitor.views);
    if (hs.length > 80) hs.slice(0, hs.length - 80).forEach((h) => delete visitor.views[h]);
    store.set('localStorage', 'sn_v1', visitor);
  };
  const pairKey = (a, b) => `${a}>${b}`;
  // s shown, o opened, c clicks, a adds, d dismissals, b combo buys (same shape as Smart Nudge)
  const pair = (a, b) => {
    const k = pairKey(a, b);
    visitor.pairs[k] = { s: 0, o: 0, c: 0, a: 0, d: 0, b: 0, ...(visitor.pairs[k] || {}) };
    return visitor.pairs[k];
  };

  /* ---------- Text helpers ---------- */
  const norm = (s) =>
    String(s || '')
      .replace(/&amp;/g, '&')
      .replace(/&#39;|&rsquo;/g, "'")
      .replace(/&[a-z]+;/g, ' ')
      .toLowerCase();
  const isAscii = (w) => /^[\x00-\x7f]+$/.test(w);
  const esc = (s) => s.replace(/[.*+?^$()|[\]\\]/g, '\\$&');
  const matcherCache = new Map();
  const matcher = (words) => {
    if (matcherCache.has(words)) return matcherCache.get(words);
    const ascii = words.filter(isAscii);
    const other = words.filter((w) => !isAscii(w));
    const re = ascii.length ? new RegExp(`(?:^|[^a-z0-9])(?:${ascii.map(esc).join('|')})(?=[^a-z0-9]|$)`) : null;
    const fn = (text) => (re && re.test(text)) || other.some((w) => text.includes(w));
    matcherCache.set(words, fn);
    return fn;
  };
  const firstKey = (dict, text) => Object.keys(dict).find((k) => text && matcher(dict[k])(text));
  const allKeys = (dict, text) => Object.keys(dict).filter((k) => matcher(dict[k])(text));
  const list = (s) =>
    String(s || '')
      .split(',')
      .map((x) => x.trim().toLowerCase())
      .filter(Boolean);
  const addUnique = (arr, extra) => (extra || []).forEach((x) => !arr.includes(x) && arr.push(x));

  const money = (cents) => {
    const r = Math.round(Number(cents) || 0) / 100;
    try {
      return `₹${r.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
    } catch {
      return `₹${r}`;
    }
  };
  const shortName = (title) =>
    String(title || '')
      .split('|')[0]
      .replace(/\([^)]*\)/g, '')
      .replace(/\s+gms?\b/i, '')
      .replace(/\s+/g, ' ')
      .trim();
  const optionLabel = (name) =>
    String(name || '')
      .replace(/^\s*choose\s+(your\s+)?/i, '')
      .replace(/\s+/g, ' ')
      .trim() || 'option';

  /* ---------- Traits ---------- */
  function traitsOf(p) {
    const tags = (p.tags || []).map((t) => String(t).toLowerCase().trim());
    const tagged = (prefix) => tags.filter((t) => t.startsWith(prefix)).map((t) => t.slice(prefix.length));
    const type = norm(p.product_type).trim();
    const head = norm([p.title, tags.join(' '), (p.variants || []).map((v) => v.title).join(' ')].join(' '));
    const body = norm(p.description);
    const all = `${type} ${head} ${body}`;

    const category =
      tagged('cat-')[0] ||
      TYPE_MAP[type] ||
      firstKey(CATEGORY_WORDS, type) ||
      firstKey(CATEGORY_WORDS, head) ||
      firstKey(CATEGORY_WORDS, body) ||
      'other';

    let moods = tagged('mood-');
    if (!moods.length) {
      moods = allKeys(MOOD_WORDS, all);
      addUnique(moods, TYPE_MOODS[type]);
      addUnique(moods, CATEGORY_MOODS[category]);
      if (category !== 'food') moods = moods.filter((m) => m !== 'sweet');
    }

    let occasions = tagged('occasion-');
    if (!occasions.length) {
      occasions = allKeys(OCCASION_WORDS, all);
      addUnique(occasions, CATEGORY_OCCASIONS[category]);
      if (moods.includes('gift')) addUnique(occasions, ['gifting']);
    }

    const created = Date.parse(p.created_at || '') || 0;
    const set = tags.includes('candle-set');
    const title = norm(p.title);
    return {
      handle: p.handle,
      category,
      moods,
      occasions,
      pairs: tagged('pair-'),
      noUpsell: tags.includes('no-upsell') || tags.includes('hidden'),
      webinar: String(p.handle || '').includes('webinar') || title.includes('webinar'),
      builder: Boolean(p.builder) || tags.includes('byo-box') || String(p.handle).includes('build-your-own'),
      addon: ADDON_HANDLES.includes(p.handle),
      byo: tags.includes('byo-candle'),
      single: type === 'candle' && !set,
      set,
      type,
      tags,
      created,
      price: Number(p.price) || 0,
      compare: Number(p.compare_at_price) || 0,
    };
  }
  const saving = (t) => (t.price > 0 && t.compare - t.price >= (t.price * MIN_SAVE_PCT) / 100 ? t.compare - t.price : 0);

  /* ---------- Context ---------- */
  function festivalNow(S) {
    const name = S.festival_name;
    if (!name || name === 'none' || !S.festival_start || !S.festival_end) return null;
    const d = today();
    if (d < String(S.festival_start).trim() || d > String(S.festival_end).trim()) return null;
    const label = (S.festival_label || '').trim() || name.charAt(0).toUpperCase() + name.slice(1);
    return { name, label, occasions: FESTIVAL_OCCASIONS[name] || ['festive'] };
  }
  function timeOfDay(h = new Date().getHours()) {
    if (h >= 5 && h < 11) return 'morning';
    if (h >= 11 && h < 17) return 'day';
    if (h >= 17 && h < 23) return 'evening';
    return 'night';
  }

  /* ---------- Variant chips ---------- */
  // null = no choice needed (1 variant); false = too many choices (link only); else the option list.
  function chipModel(p) {
    const count = Number(p.variant_count || (p.variants || []).length);
    if (count <= 1) return null;
    const options = p.options || [];
    if (!options.length || options.length > CHIP_MAX_OPTIONS || count > CHIP_MAX_VARIANTS || (p.variants || []).length < count) return false;
    return options.map((o) => ({ name: o.name, label: optionLabel(o.name), values: o.values || [] }));
  }
  const resolveVariant = (p, sel) =>
    (p.variants || []).find((v) => (v.options || []).every((val, i) => sel[i] === val)) || null;

  /* ---------- Scoring ---------- */
  const complementary = (a, b) =>
    (a.includes('gift') && b.length) ||
    (b.includes('gift') && a.length) ||
    COMPLEMENT.some(([x, y]) => (a.includes(x) && b.includes(y)) || (a.includes(y) && b.includes(x)));

  // Global exclusions (no anchor needed). Returns a reason string or ''.
  function excluded(p, t, ctx) {
    if (t.addon) return 'Talbeena add-on pack';
    if (ctx.inCart.has(p.handle)) return 'in cart';
    if (!p.available) return 'unavailable';
    if (t.webinar) return 'webinar';
    if (t.noUpsell || ctx.exHandles.includes(p.handle) || t.tags.some((x) => ctx.exTags.includes(x))) return 'excluded';
    if (!(t.price > 0)) return 'price 0';
    return '';
  }

  // Score p against one anchor (a cart line). Same rules as Smart Nudge scoreAll(); null = not allowed for this anchor.
  function scoreAgainst(anchor, p, t, ctx) {
    const cur = anchor.t;
    const curPrice = anchor.price;
    if (cur.category === 'candles' && !CANDLE_CATEGORIES.includes(t.category)) return null;
    const pr = visitor.pairs[pairKey(cur.handle, p.handle)];
    if (pr && pr.d >= 2) return null;
    const sameCat = t.category === cur.category;
    const upgrade = sameCat && curPrice > 0 && t.price > curPrice && t.price <= curPrice * 1.3;
    const smallAddon = sameCat && t.single && curPrice > 0 && t.price <= curPrice * SMALL_ADDON_SHARE;
    if (sameCat && !upgrade && !smallAddon) return null; // no downsell
    if (!upgrade && curPrice > 0 && t.price > curPrice * TOO_PRICEY) return null;

    let s = 0;
    const why = [];
    const add = (n, r) => {
      s += n;
      why.push(`${n > 0 ? '+' : ''}${n} ${r}`);
    };
    if (cur.pairs.includes(p.handle) || t.pairs.includes(cur.handle)) add(50, 'manual pair');
    if (t.category !== cur.category) add(30, `other category (${t.category})`);
    if (complementary(cur.moods, t.moods)) add(25, `moods ${cur.moods.join('/') || '-'} ↔ ${t.moods.join('/')}`);
    if (ctx.festival && t.occasions.some((o) => ctx.festival.occasions.includes(o))) add(25, `${ctx.festival.name} occasion`);
    if (curPrice > 0 && t.price <= curPrice * 0.5) add(20, 'impulse (≤50% price)');
    if (upgrade) add(15, 'upgrade (100–130% price)');
    if (PREFERRED_PAIRS.includes(`${cur.category}>${t.category}`) && (!sameCat || smallAddon)) add(20, `preferred pair ${cur.category}→${smallAddon ? 'small candle' : t.category}`);
    if (!upgrade && curPrice > 0 && t.price > curPrice * PRICEY) add(-30, `over ${PRICEY}x price`);
    if (visitor.views[p.handle]) add(15, 'viewed before');
    if (saving(t)) add(10, 'real saving');
    if ((ctx.tod === 'evening' && t.moods.includes('glow')) || (ctx.tod === 'morning' && t.category === 'food')) add(10, `${ctx.tod} fit`);
    if (t.created && Date.now() - t.created <= 30 * DAY) add(5, 'new (30 days)');
    if (pr && pr.d === 1) add(-40, 'dismissed once');
    return { score: s, upgrade, smallAddon, why };
  }

  // Candle-only cart: slot 1 = upgrade, else the best Talbeena; at least 1 Talbeena; never 3 of one kind.
  const kindOf = (r) => (r.t.category === 'food' ? 'food' : r.t.byo || r.t.single ? 'single' : 'combo');
  function pickCandle(rows, n) {
    const ranked = rows.filter((r) => r.total >= MIN_SCORE).sort((a, b) => b.total - a.total);
    const out = [];
    const count = (k) => out.filter((o) => kindOf(o) === k).length;
    const fits = (r) => !out.includes(r) && count(kindOf(r)) < 2;
    const first = ranked.find((r) => r.upgrade) || ranked.find((r) => kindOf(r) === 'food') || ranked[0];
    if (first) out.push(first);
    if (out.length < n && !count('food')) {
      const food = ranked.find((r) => kindOf(r) === 'food' && fits(r));
      if (food) out.push(food);
    }
    for (const r of ranked) {
      if (out.length >= n) break;
      if (fits(r)) out.push(r);
    }
    return out;
  }

  // Greedy pick: best score first, -20 for a category already picked, max 1 BYO single.
  function pick(rows, n) {
    const out = [];
    const pool = rows.slice();
    while (out.length < n && pool.length) {
      pool.forEach((r) => {
        r.eff = r.total;
        if (out.some((o) => o.t.category === r.t.category)) r.eff -= 20;
        if (r.t.byo && out.some((o) => o.t.byo)) r.eff -= 1000;
      });
      pool.sort((a, b) => b.eff - a.eff);
      const next = pool.shift();
      if (next.eff < MIN_SCORE) break;
      out.push(next);
    }
    return out;
  }

  /* ---------- Message line ---------- */
  const matchesTrait = (t, v) => v === 'any' || t.category === v || t.moods.includes(v);
  function chooseLine(cur, top, ctx, sess, merchantLines) {
    const vals = {
      name: shortName(top.p.title),
      price: top.t.price > 0 ? money(top.t.price) : '',
      save: saving(top.t) ? money(saving(top.t)) : '',
      extra: top.upgrade ? money(top.t.price - top.anchor.price) : '',
      festival: ctx.festival ? ctx.festival.label : '',
      total: top.t.price > 0 && ctx.cartTotal > 0 ? money(ctx.cartTotal + top.t.price) : '',
    };
    const fill = (text) => {
      let ok = true;
      const out = text.replace(/\[(\w+)\]/g, (m, k) => {
        if (!(k in vals) || !vals[k]) ok = false;
        return vals[k] || '';
      });
      return ok ? out : null;
    };
    const lines = [...(merchantLines || []).map((l) => ({ ...l, merchant: true })), ...BANK];
    const start = (sess.n || 0) % 3;
    const order = [0, 1, 2].map((i) => LANGS[(start + i) % 3]);
    for (const lang of order) {
      const cands = [];
      for (const l of lines) {
        if (l.lang !== lang || (sess.lines || []).includes(l.id)) continue;
        if (!matchesTrait(cur, l.from || 'any')) continue;
        const to = l.to || 'any';
        // "₹X aur mein" lines only for a real upgrade (the old line is replaced); "single" = small candle add-on
        const single = top.smallAddon || (top.t.byo && (top.kind === 'add' || top.kind === 'chips'));
        if (to === 'upgrade' ? top.kind !== 'upgrade' : to === 'single' ? !single : to === 'set' ? !top.t.set : !matchesTrait(top.t, to)) continue;
        const text = fill(l.text || '');
        if (!text) continue;
        const spec =
          (l.from !== 'any' ? 1 : 0) + (to !== 'any' ? 2 : 0) + (ctx.festival && l.text.includes('[festival]') ? 1 : 0) +
          (l.merchant ? 0.5 : 0) - (visitor.lines[l.id] || 0) * 0.1 + Math.random() * 0.05;
        cands.push({ l, text, spec });
      }
      if (cands.length) {
        cands.sort((a, b) => b.spec - a.spec);
        return { ...cands[0].l, text: cands[0].text };
      }
    }
    return null;
  }

  /* ---------- Cart → anchors ---------- */
  const isAddonLine = (item) => {
    const pr = item?.properties || {};
    return Boolean(pr['Linked combo'] || pr._addon === '1');
  };
  function anchorsFrom(cart, byHandle) {
    const seen = new Set();
    const out = [];
    (cart?.items || []).forEach((item) => {
      if (isAddonLine(item) || seen.has(item.handle)) return;
      seen.add(item.handle);
      const props = item.properties || {};
      const p = byHandle.get(item.handle) || {
        handle: item.handle,
        title: item.product_title,
        product_type: item.product_type,
        tags: [],
        price: item.price,
        compare_at_price: 0,
        variants: [],
        description: '',
      };
      out.push({
        t: traitsOf(p),
        price: Number(item.price) || Number(p.price) || 0,
        box: !!props._box_id,
        // A plain line (no properties: not a Talbeena combo, not part of an Atelier box) can be swapped for an upgrade
        line: Object.keys(props).length ? null : { key: item.key, quantity: Number(item.quantity) || 1, variant_id: item.variant_id },
      });
    });
    return out;
  }

  // Owner rule (same as ga-cart-guard.js): BYO candles need no ₹599 box minimum when the cart also has
  // (1) a non-BYO candle product (type "Candle Combo", or a "Candle" tagged candle-set) or (2) a Talbeena combo.
  const unlocksByo = (a) =>
    !a.box && !a.t.byo && !a.t.builder && (a.t.type === 'candle combo' || (a.t.type === 'candle' && a.t.set) || a.t.type === 'food & nutrition');

  /**
   * suggest({ catalog, cart, settings, sess, lines, max })
   * → { picks: [{ p, t, anchor, score, total, upgrade, smallAddon, why, chips, kind }], line, primary, ctx, skipped }
   * kind: 'add' (one tap) | 'chips' | 'flavours' (Talbeena link) | 'atelier' (BYO link) | 'link'
   */
  function suggest({ catalog, cart, settings = {}, sess = {}, lines = [], max = 3, atelierUrl = '' }) {
    const byHandle = new Map((catalog || []).map((p) => [p.handle, p]));
    const anchors = anchorsFrom(cart, byHandle);
    const ctx = {
      festival: festivalNow(settings),
      tod: timeOfDay(),
      inCart: new Set((cart?.items || []).map((i) => i.handle)),
      exTags: list(settings.exclude_tags),
      exHandles: list(settings.exclude_handles),
      cartTotal: Number(cart?.total_price) || 0,
    };
    const skipped = [];
    if (!anchors.length) return { picks: [], line: null, primary: null, ctx, skipped };
    ctx.byoOpen = anchors.some(unlocksByo);

    const rows = [];
    for (const p of catalog || []) {
      const t = traitsOf(p);
      const reason = excluded(p, t, ctx);
      if (reason) {
        skipped.push({ handle: p.handle, reason });
        continue;
      }
      let best = null;
      for (const a of anchors) {
        const r = scoreAgainst(a, p, t, ctx);
        if (r && (!best || r.score > best.score)) best = { ...r, anchor: a };
      }
      if (!best) {
        skipped.push({ handle: p.handle, reason: 'no anchor allows it (candle rule / no downsell / price)' });
        continue;
      }
      const chips = chipModel(p);
      const base = chips === false ? 'link' : chips ? 'chips' : 'add';
      let kind;
      if (t.category === 'food') kind = 'flavours';
      else if (t.byo) kind = ctx.byoOpen ? base : atelierUrl ? 'atelier' : 'link'; // one-tap only when no box minimum applies
      else kind = base;
      // Real upgrade: same product type, one variant, and a plain cart line to replace (e.g. Festive -> Grand)
      let replace = null;
      if (best.upgrade && kind === 'add' && best.anchor.line && t.type === best.anchor.t.type && !t.byo && !best.anchor.t.byo) {
        kind = 'upgrade';
        replace = best.anchor.line;
      }
      rows.push({ p, t, ...best, total: best.score + Math.random() * 0.9, chips, kind, replace, diff: t.price - best.anchor.price, sel: [] });
    }
    const candleOnly = anchors.every((a) => a.t.category === 'candles');
    const picks = candleOnly ? pickCandle(rows, max) : pick(rows, max);
    // Primary anchor for the message: the most expensive cart line.
    const primary = anchors.slice().sort((a, b) => b.price - a.price)[0];
    const line = picks.length ? chooseLine(picks[0].anchor.t, picks[0], ctx, sess, lines) : null;
    return { picks, line, primary, ctx, skipped, rows };
  }

  // A clicked suggestion link that later shows up in the cart counts as an add for that pair (once).
  function creditAdds(cart, onAdd) {
    let changed = false;
    (cart?.items || []).forEach((item) => {
      const from = visitor.clicked[item.handle];
      if (!from) return;
      pair(from, item.handle).a++;
      delete visitor.clicked[item.handle];
      changed = true;
      onAdd?.(from, item.handle);
    });
    if (changed) saveVisitor();
  }

  return {
    LANGS,
    LANG_ATTR,
    store,
    visitor,
    saveVisitor,
    pair,
    traitsOf,
    saving,
    chipModel,
    resolveVariant,
    shortName,
    money,
    suggest,
    creditAdds,
  };
})();
